/** What a mission does to the people: wounds, fatigue, trait timers, captivity and the wall (04 §3, §4.3-§4.4). */

import { Pcg32 } from '../../sim/rng';
import { EPITAPHS, EPITAPHS_LOST, type HazardRules } from '../content/career';
import { RECOVERY } from '../content/economy';
import { TRAIT_RULES } from '../content/traits';
import { type FullSummary, fullName, type OperationState, type ProfileState, snapshot } from '../state';
import { type MemorialEntry, type RangerRecord, RANKS } from '../types';
import { fillTemplate } from '../util';
import { giveMedal } from './medals';

/** Wound state on extraction (04 §4.4): fit or lightly wounded flies again; worse sits out. */
export function applyWounds(ranger: RangerRecord, summary: FullSummary, rules: HazardRules, rng: Pcg32): void {
  ranger.record.woundsTaken += Math.max(summary.woundsAtEnd, summary.wasDowned ? 3 : 0);
  if (summary.wasDowned) {
    ranger.status = 'critical';
    ranger.recoveryMissions = RECOVERY.critical + rules.extraRecovery;
    if (rng.chance(TRAIT_RULES.oldWoundChance) && !ranger.traits.includes('old-wound')) ranger.traits.push('old-wound');
    if (summary.downedByExplosion && !ranger.traits.includes('tinnitus')) ranger.traits.push('tinnitus');
  } else if (summary.woundsAtEnd >= RECOVERY.seriousWounds) {
    ranger.status = 'wounded';
    ranger.recoveryMissions = RECOVERY.serious + rules.extraRecovery;
  } else {
    ranger.status = 'ready';
    ranger.recoveryMissions = 0;
  }
}

/** The ranger who flew: fatigue up, his own trait countdowns down, Green shed, Blooded earned. */
export function afterFlying(ranger: RangerRecord, extracted: boolean): void {
  ranger.fatigue += 1;
  for (const trait of Object.keys(ranger.traitTimers) as Array<keyof typeof ranger.traitTimers>) {
    const left = (ranger.traitTimers[trait] ?? 0) - 1;
    if (left > 0) ranger.traitTimers[trait] = left;
    else {
      delete ranger.traitTimers[trait];
      ranger.traits = ranger.traits.filter((t) => t !== trait);
    }
  }
  if (extracted) ranger.traits = ranger.traits.filter((t) => t !== 'green');
  if (ranger.record.missions >= TRAIT_RULES.bloodedAfter && !ranger.traits.includes('blooded')) {
    ranger.traits = ranger.traits.filter((t) => !TRAIT_RULES.bloodedCancels.includes(t));
    ranger.traits.push('blooded');
  }
}

/** One recovery step: a sat-out mission, a Rest & Refit, or a Priority Medevac. Returns true if he is back. */
export function recoveryStep(ranger: RangerRecord, op: OperationState): boolean {
  if (ranger.status !== 'wounded' && ranger.status !== 'critical' && ranger.status !== 'mia') return false;
  ranger.recoveryMissions = Math.max(0, ranger.recoveryMissions - 1);
  if (ranger.recoveryMissions > 0) return false;
  if (ranger.status === 'mia') {
    giveMedal(ranger, 'evaders-ribbon');
    op.log.push(`${fullName(ranger)} walked back into camp.`);
  } else op.log.push(`${fullName(ranger)} is fit for duty.`);
  ranger.status = 'ready';
  return true;
}

/** Everyone who sat this mission out: rested a little, healed a little. */
export function afterSittingOut(op: OperationState, flownId: string): void {
  for (const ranger of op.company) {
    if (ranger.id === flownId) continue;
    ranger.fatigue = Math.max(0, ranger.fatigue - 1);
    recoveryStep(ranger, op);
  }
}

/** A successful camp raid brings every captive home, Shaken and decorated for it (04 §2.2). */
export function freeCaptives(op: OperationState): RangerRecord[] {
  const freed = op.company.filter((r) => r.status === 'pow');
  for (const ranger of freed) {
    ranger.status = 'ready';
    ranger.recoveryMissions = 0;
    ranger.fatigue = 0;
    giveMedal(ranger, 'pow-medal');
    if (!ranger.traits.includes('blooded') && !ranger.traits.includes('shaken')) ranger.traits.push('shaken');
    if (ranger.traits.includes('shaken')) ranger.traitTimers.shaken = TRAIT_RULES.shakenMissions;
    op.log.push(`${fullName(ranger)} is free.`);
  }
  return freed;
}

function epitaphFor(ranger: RangerRecord, fate: MemorialEntry['fate']): string {
  const rng = new Pcg32(ranger.seed, 0x0e91);
  return fillTemplate(rng.pick(fate === 'kia' ? EPITAPHS : EPITAPHS_LOST), {
    name: ranger.surname, callsign: ranger.callsign, rank: RANKS[ranger.rank] ?? RANKS[0],
    missions: ranger.record.missions, kills: ranger.record.kills,
  });
}

/** Take a ranger off the company and onto the Memorial Wall, newest first. Never undone. */
export function toMemorial(
  profile: ProfileState, op: OperationState | null, ranger: RangerRecord, fate: MemorialEntry['fate'],
  mission: string, cause: string, photo?: string,
): MemorialEntry {
  ranger.status = fate;
  ranger.pendingPerkChoices = [];
  const entry: MemorialEntry = {
    ranger: snapshot(ranger), fate, operation: op?.codename ?? 'CLASSIC', mission, cause,
    epitaph: epitaphFor(ranger, fate),
    ...(photo === undefined ? {} : { photo }),
  };
  profile.memorial.unshift(entry);
  if (op) {
    op.company = op.company.filter((r) => r.id !== ranger.id);
    op.log.push(`${fullName(ranger)} ${fate === 'kia' ? 'was killed in action' : 'will not be coming home'}.`);
  }
  return entry;
}

/** The camp was alerted, or the theatre was left behind: the captives are gone for good. */
export function loseCaptives(profile: ProfileState, op: OperationState, why: string): void {
  for (const ranger of op.company.filter((r) => r.status === 'pow')) {
    toMemorial(profile, op, ranger, 'lost', 'P.O.W. - never recovered', why);
  }
}
